import { createStore, createEffect } from "effector"
import { useUnit } from "effector-react"

import AssetService from "services/AssetService"
import UserService from "services/UserService"
import TenantSettings from "models/TenantSettings"
import TenantContacts, { ITenantContacts } from "models/TenantContacts"
import TermsAndConditions, { IUpdateTermsAndConditions } from "models/TermsAndConditions"
import CustomError from "models/CustomError"
import { ErrorCode, UserPermission } from "config/constants"
import TenantEmployees, { IUpdateTenantEmployeesDto } from "models/TenantEmployees"
import TenantWarehouses from "models/TenantWarehouses"
import DeliveryTerms from "models/DeliveryTerms"
import TenantInvoicePositions from "models/TenantInvoicePositions"
import { ICreateTenantInvoicePositionDto, IUpdateTenantInvoicePositionDto } from "models/TenantInvoicePosition"
import { ICreateTenantWarehouseDto, ITenantWarehouseDto } from "models/TenantWarehouse"
import OrderTypes from "models/OrderTypes"
import { ICreateOrderTypeDto } from "models/OrderType"
import PaymentTerm from "models/PaymentTerm"
import Country from "models/Country"
import TenantSettingsLanguages, { IUpdateTenantSettingsLanguagesDto } from "models/TenantSettingsLanguages"
import { IApiLink } from "@/types"
import { ICreateUpdateDeliveryTermDto } from "models/DeliveryTerm"
import Commissions, { IUpdateCommissionsDto } from "models/Commissions"
import { usePermissions } from "./permissions"
import TenantSettingsGlobalAssetOverview, { IUpdateTenantSettingsGlobalAssetOverviewDto } from "models/TenantSettingsGlobalAssetOverview"
import userStore from "stores/user"

// Defaults
const defaultTenantLanguages = new TenantSettingsLanguages({
    primaryLanguage: {
        isoCulture: "en",
        description: "English"
    },
    maintainableLanguages: [],
    links: [],
})

// Stores
const $tenantSettings = createStore<TenantSettings | null>(null)
const $tenantSettingsLanguages = createStore<TenantSettingsLanguages | null>(null)
const $tenantContacts = createStore<TenantContacts | null>(null)
const $tenantEmployees = createStore<TenantEmployees | null>(null)
const $tenantWarehouses = createStore<TenantWarehouses | null>(null)
const $termsAndConditions = createStore<TermsAndConditions | null>(null)
const $deliveryTerms = createStore<DeliveryTerms | null>(null)
const $paymentTerms = createStore<PaymentTerm[] | null>(null)
const $invoicePositions = createStore<TenantInvoicePositions | null>(null)
const $orderTypes = createStore<OrderTypes | null>(null)
const $countriesWithInternalCode = createStore<Country[] | null>(null)
const $commissions = createStore<Commissions | null>(null)
const $globalAssetOverview = createStore<TenantSettingsGlobalAssetOverview | null>(null)

// Effects
const fetchTenantSettings = createEffect(async () => {
    return UserService.getTenantSettings()
})

const fetchTenantSettingsLanguages = createEffect(async () => {
    return UserService.getTenantSettingsLanguages()
})

const updateTenantSettingsLanguages = createEffect(async (data: IUpdateTenantSettingsLanguagesDto) => {
    const linkUpdate = $tenantSettingsLanguages.getState()?.linkUpdate

    if (!linkUpdate) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    await UserService.updateTenantSettingsLanguages(linkUpdate, data)
    return fetchTenantSettingsLanguages()
})

const fetchTenantContacts = createEffect(async () => {
    return UserService.getTenantContacts()
})

const updateTenantContacts = createEffect((data: ITenantContacts) => {
    const linkUpdate = $tenantContacts.getState()?.linkUpdate

    if (!linkUpdate) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    const tenantContacts = $tenantContacts.getState()
    return UserService.updateTenantContacts(linkUpdate, data, tenantContacts?.eTag)
})

const fetchTenantEmployees = createEffect(() => {
    return UserService.getTenantEmployees()
})

const updateTenantEmployees = createEffect((data: IUpdateTenantEmployeesDto) => {
    const linkUpdate = $tenantEmployees.getState()?.linkUpdate

    if (!linkUpdate) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    return UserService.updateTenantEmployees(linkUpdate, data)
})

const fetchTenantWarehouses = createEffect(() => {
    return AssetService.getTenantWarehouses()
})

const createTenantWarehouse = createEffect(async (data: ICreateTenantWarehouseDto) => {
    const link = $tenantWarehouses.getState()?.linkCreate

    if (!link) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    await AssetService.createTenantWarehouse(link, data)
    return fetchTenantWarehouses()
})

const updateTenantWarehouse = createEffect(async (data: ITenantWarehouseDto) => {
    const link = $tenantWarehouses.getState()?.linkUpdate

    if (!link) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    await AssetService.updateTenantWarehouse(link, data)
    return fetchTenantWarehouses()
})

const fetchInvoicePositions = createEffect(() => {
    return UserService.getTenantInvoicePositions()
})

const createInvoicePosition = createEffect(async (data: ICreateTenantInvoicePositionDto) => {
    const linkCreate = $invoicePositions.getState()?.linkCreate

    if (!linkCreate) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    await UserService.createTenantInvoicePosition(linkCreate, data)
    return fetchInvoicePositions()
})

const updateInvoicePosition = createEffect(async ({ link, data }: {
    link: IApiLink
    data: IUpdateTenantInvoicePositionDto
}) => {
    await UserService.updateTenantInvoicePosition(link, data)
    return fetchInvoicePositions()
})

const fetchOrderTypes = createEffect(() => {
    return UserService.getOrderTypes()
})

const createOrderType = createEffect(async (data: ICreateOrderTypeDto) => {
    const linkCreate = $orderTypes.getState()?.linkCreate

    if (!linkCreate) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    await UserService.createOrderType(linkCreate, data)
    return fetchOrderTypes()
})

const fetchTermsAndConditions = createEffect(() => {
    return UserService.getTermsAndConditions()
})

const updateTermsAndConditions = createEffect((data: IUpdateTermsAndConditions) => {
    const termsAndConditions = $termsAndConditions.getState()

    if (!termsAndConditions?.linkUpdate) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    return UserService.updateTermsAndConditions(termsAndConditions.linkUpdate, data)
})

const fetchDeliveryTerms = createEffect(async () => {
    const deliveryTerms = await UserService.getDeliveryTerms()
    return deliveryTerms
})

const createUpdateDeliveryTerm = createEffect(async ({ link, data }: { link: IApiLink, data: ICreateUpdateDeliveryTermDto }) => {
    await UserService.createUpdateDeliveryTerm(link, data)
    return fetchDeliveryTerms()
})

const fetchPaymentTerms = createEffect(async () => {
    let paymentTerms = $paymentTerms.getState()
    if (paymentTerms) {
        return paymentTerms
    }
    paymentTerms = await UserService.getPaymentTerms()
    return paymentTerms
})

const fetchCountriesWithInternalCode = createEffect(() => {
    return UserService.getCountries({
        onlyWithInternalCode: true,
    })
})

const fetchCommissions = createEffect(async () => {
    const commissions = await AssetService.getCommissions()
    return commissions
})

const updateCommissions = createEffect(async (commissions: IUpdateCommissionsDto) => {
    const linkUpdateCommissions = $commissions.getState()?.linkUpdateCommissions

    if (!linkUpdateCommissions) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }
    await AssetService.updateCommissions(linkUpdateCommissions, commissions)
    await fetchCommissions()
})

const fetchGlobalAssetOverview = createEffect(() => {
    const user = userStore.$user.getState()
    if (!user?.hasPermission(UserPermission.TenantSettingsGlobalAssetOverviewView)) {
        return null
    }
    return UserService.getTenantSettingsGlobalAssetOverview()
})

const updateGlobalAssetOverview = createEffect(async (data: IUpdateTenantSettingsGlobalAssetOverviewDto) => {
    const linkUpdate = $globalAssetOverview.getState()?.linkUpdate

    if (!linkUpdate) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }
    await UserService.updateTenantSettingsGlobalAssetOverview(linkUpdate, data)
    await fetchGlobalAssetOverview()
})


// Listeners
$tenantSettings.on(fetchTenantSettings.doneData, (_, payload) => payload)
$tenantSettingsLanguages.on(fetchTenantSettingsLanguages.doneData, (_, payload) => payload)
$tenantContacts.on(fetchTenantContacts.doneData, (_, payload) => payload)
$tenantContacts.on(updateTenantContacts.doneData, (_, payload) => payload)
$tenantEmployees.on(fetchTenantEmployees.doneData, (_, payload) => payload)
$tenantEmployees.on(updateTenantEmployees.doneData, (_, payload) => payload)
$tenantWarehouses.on(fetchTenantWarehouses.doneData, (_, payload) => payload)
$tenantWarehouses.on(createTenantWarehouse.doneData, (_, payload) => payload)
$tenantWarehouses.on(updateTenantWarehouse.doneData, (_, payload) => payload)
$termsAndConditions.on(fetchTermsAndConditions.doneData, (_, payload) => payload)
$termsAndConditions.on(updateTermsAndConditions.doneData, (_, payload) => payload)
$deliveryTerms.on(fetchDeliveryTerms.doneData, (_, payload) => payload)
$paymentTerms.on(fetchPaymentTerms.doneData, (_, payload) => payload)
$invoicePositions.on(fetchInvoicePositions.doneData, (_, payload) => payload)
$orderTypes.on(fetchOrderTypes.doneData, (_, payload) => payload)
$orderTypes.on(createOrderType.doneData, (_, payload) => payload)
$countriesWithInternalCode.on(fetchCountriesWithInternalCode.doneData, (_, payload) => payload)
$commissions.on(fetchCommissions.doneData, (_, payload) => payload)
$globalAssetOverview.on(fetchGlobalAssetOverview.doneData, (_, payload) => payload)

// Exports


export function useTenantSettings() {
    const termsAndConditions = useUnit($termsAndConditions)
    const tenantContacts = useUnit($tenantContacts)
    const tenantEmployees = useUnit($tenantEmployees)
    const tenantSettingsLanguages = useUnit($tenantSettingsLanguages) || defaultTenantLanguages
    const tenantWarehouses = useUnit($tenantWarehouses)
    const deliveryTerms = useUnit($deliveryTerms)
    const { hasPermission } = usePermissions()

    return {
        tenantSettings: useUnit($tenantSettings),
        tenantSettingsLanguages,
        tenantPrimaryLanguageIsoCode: useUnit($tenantSettingsLanguages)?.primaryLanguage.isoCode,
        tenantContacts,
        tenantEmployees,
        tenantWarehouses,
        termsAndConditions,
        deliveryTerms,
        paymentTerms: useUnit($paymentTerms),
        invoicePositions: useUnit($invoicePositions),
        orderTypes: useUnit($orderTypes),
        countriesWithInternalCode: useUnit($countriesWithInternalCode),
        commissions: useUnit($commissions),
        fetchTenantSettings,
        fetchTenantSettingsLanguages,
        updateTenantSettingsLanguages: tenantSettingsLanguages.linkUpdate ? updateTenantSettingsLanguages : undefined,
        fetchTenantContacts,
        updateTenantContacts: tenantContacts?.linkUpdate ? updateTenantContacts : undefined,
        fetchTenantEmployees,
        updateTenantEmployees: tenantEmployees?.linkUpdate ? updateTenantEmployees : undefined,
        fetchTenantWarehouses,
        createTenantWarehouse: tenantWarehouses?.linkCreate ? createTenantWarehouse : undefined,
        updateTenantWarehouse: tenantWarehouses?.linkUpdate ? updateTenantWarehouse : undefined,
        fetchTermsAndConditions,
        updateTermsAndConditions: hasPermission(UserPermission.TenantSettingsOtherShippingDetailsEdit) ? updateTermsAndConditions : undefined,
        fetchDeliveryTerms,
        createDeliveryTerm: hasPermission(UserPermission.TenantSettingsTermsOfDeliveryEdit) && deliveryTerms?.linkCreate ? createUpdateDeliveryTerm : undefined,
        updateDeliveryTerm: hasPermission(UserPermission.TenantSettingsTermsOfDeliveryEdit) ? createUpdateDeliveryTerm : undefined,
        fetchPaymentTerms,
        fetchInvoicePositions,
        createInvoicePosition,
        updateInvoicePosition,
        fetchOrderTypes,
        createOrderType,
        fetchCountriesWithInternalCode,
        fetchCommissions,
        globalAssetOverview: useUnit($globalAssetOverview),
        fetchGlobalAssetOverview: hasPermission(UserPermission.TenantSettingsGlobalAssetOverviewView) ? fetchGlobalAssetOverview : undefined,
        updateGlobalAssetOverview: hasPermission(UserPermission.TenantSettingsGlobalAssetOverviewEdit) ? updateGlobalAssetOverview : undefined,
        updateEvaluationParametersInHouse: hasPermission(UserPermission.EvaluationParametersInHouseEdit),
        updateEvaluationParametersTradeAssets: hasPermission(UserPermission.EvaluationParametersTradeAssetsEdit),
        updateOverheadFactors: hasPermission(UserPermission.OverheadFactorsEdit),
        updateFinancialFactors: hasPermission(UserPermission.FinancialFactorsEdit),
        updateInvoicePositions: hasPermission(UserPermission.InvoicePositionsEdit),
        updateCollectiveReferenceNumbers: hasPermission(UserPermission.CollectiveReferenceNumbersEdit),
        updateCostCategories: hasPermission(UserPermission.CostCategoriesEdit),
        updateDepreciationPeriod: hasPermission(UserPermission.DepreciationPeriodEdit),
        updateAssetCategories: hasPermission(UserPermission.AssetCategoriesEdit),
        updateAssetTypes: hasPermission(UserPermission.AssetTypesEdit),
        updateTBDInHouse: hasPermission(UserPermission.TBDInHouseEdit),
        updateTBDTradeAssets: hasPermission(UserPermission.TBDTradeAssetsEdit),
        updateDepreciationMatrix: hasPermission(UserPermission.DepreciationEdit),
        updateOrderTypes: hasPermission(UserPermission.OrderTypesEdit),
        updateFairValueFactors: hasPermission(UserPermission.FairValueFactorsEdit)
    }
}

export default {
    $tenantSettings,
    $tenantSettingsLanguages,
    fetchTenantSettings,
    fetchTenantSettingsLanguages,
    fetchCommissions,
    updateCommissions,
    fetchGlobalAssetOverview,
    $tenantEmployees,
    fetchTenantEmployees,
}
